<template>
  <!-- ===================================================================
       MON COMPTE — écran de l'application, balisage propre.
       Le site garde le sien, intact, dans le `v-else` plus bas.

       Le solde de crédits est traité comme un élément fort et non comme une
       ligne de formulaire : c'est ce qui sert à payer une sortie.
       Les liens légaux vivent ici, le pied de page du site n'étant pas
       affiché dans l'application — Google Play exige que la politique de
       confidentialité soit accessible depuis l'app.
       =================================================================== -->
  <div v-if="isNative" class="cp-page">

    <header class="cp-top">
      <h1 class="cp-top__title">Mon compte</h1>
    </header>

    <section class="cp-solde">
      <span class="cp-solde__bulle" aria-hidden="true"></span>
      <span class="cp-solde__gauche">
        <span class="cp-solde__label">Solde de crédits</span>
        <span class="cp-solde__montant">{{ creditsBalance || 0 }}<span>&nbsp;€</span></span>
        <span class="cp-solde__note">Utilisable au moment du paiement</span>
      </span>
      <button type="button" class="cp-solde__ajout" @click="openModal">+ Créditer</button>
    </section>

    <div class="cp-onglets" role="tablist" aria-label="Sections du compte">
      <span class="cp-onglets__marque" :class="{ 'is-right': activeSection === 'info' }" aria-hidden="true"></span>
      <button
        type="button" role="tab" class="cp-onglet"
        :class="{ 'is-on': activeSection === 'reservations' }"
        :aria-selected="activeSection === 'reservations'"
        @click="setActiveSection('reservations')"
      >
        Mes sorties
        <span v-if="cpAVenir.length" class="cp-onglet__nb">{{ cpAVenir.length }}</span>
      </button>
      <button
        type="button" role="tab" class="cp-onglet"
        :class="{ 'is-on': activeSection === 'info' }"
        :aria-selected="activeSection === 'info'"
        @click="setActiveSection('info')"
      >Mes infos</button>
    </div>

    <!-- ---------- Réservations ---------- -->
    <section v-if="activeSection === 'reservations'" class="cp-bloc">
      <template v-if="cpAVenir.length">
        <h2 class="cp-titre">À venir</h2>
        <ul class="cp-resas">
          <li
            v-for="offer in cpAVenir"
            :key="offer.id"
            class="cp-resa"
            :class="{ 'is-annulee': offer.status === 'CANCELED' }"
          >
            <div class="cp-resa__haut" @click="navigateToOfferDetails(offer.id)">
              <span class="cp-resa__vignette">
                <img v-if="offer.photos && offer.photos[0]" :src="offer.photos[0].imageUrl" alt="" loading="lazy">
                <span v-if="offer.type" class="cp-resa__type">{{ offer.type }}</span>
              </span>
              <span class="cp-resa__corps">
                <span class="cp-resa__titre">{{ offer.titleOffers || offer.sport }}</span>
                <span class="cp-resa__ligne">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/></svg>
                  {{ offer.formattedDate }}
                </span>
                <span v-if="offer.city" class="cp-resa__ligne">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M20.5 10.5c0 6.5-8.5 12-8.5 12s-8.5-5.5-8.5-12a8.5 8.5 0 0 1 17 0z"/><circle cx="12" cy="10.3" r="2.8"/></svg>
                  {{ offer.city }}<template v-if="offer.postcode"> ({{ String(offer.postcode).slice(0, 2) }})</template>
                </span>
                <span class="cp-resa__etat" :class="offer.status === 'CANCELED' ? 'is-ko' : 'is-ok'">
                  {{ offer.status === 'CANCELED' ? 'Annulée' : 'Inscription confirmée' }}
                </span>
              </span>
            </div>

            <div class="cp-resa__bas">
              <span class="cp-resa__prix">
                Payé <strong>{{ offer.amount }} €</strong>
                <template v-if="offer.quantity"> · {{ offer.quantity }} place<template v-if="offer.quantity > 1">s</template></template>
              </span>
              <button
                v-if="offer.status !== 'CANCELED'"
                type="button" class="cp-resa__annuler"
                @click="selectedOffer = offer; openConfirmationModal()"
              >Annuler</button>
            </div>
          </li>
        </ul>
      </template>

      <div v-else class="cp-vide">
        <span class="cp-vide__rond" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/></svg>
        </span>
        <h2 class="cp-vide__titre">Aucune sortie réservée</h2>
        <p class="cp-vide__texte">Vos prochaines activités s'afficheront ici. Votre solde de crédits peut servir à en réserver une.</p>
        <RouterLink to="/OffersList" class="cp-vide__cta">Explorer le catalogue</RouterLink>
      </div>

      <template v-if="cpPassees.length">
        <h2 class="cp-titre cp-titre--espace">Passées</h2>
        <ul class="cp-resas">
          <li v-for="offer in cpPassees" :key="`p-${offer.id}`" class="cp-resa is-passee">
            <div class="cp-resa__haut" @click="navigateToOfferDetails(offer.id)">
              <span class="cp-resa__vignette">
                <img v-if="offer.photos && offer.photos[0]" :src="offer.photos[0].imageUrl" alt="" loading="lazy">
              </span>
              <span class="cp-resa__corps">
                <span class="cp-resa__titre">{{ offer.titleOffers || offer.sport }}</span>
                <span class="cp-resa__ligne">{{ offer.formattedDate }}</span>
                <span v-if="offer.city" class="cp-resa__ligne">{{ offer.city }}</span>
              </span>
            </div>
          </li>
        </ul>
      </template>
    </section>

    <!-- ---------- Informations ---------- -->
    <section v-else class="cp-bloc">
      <div class="cp-profil">
        <span class="cp-profil__photo">
          <img v-if="cpPhoto" :src="cpPhoto" alt="">
          <span v-else class="cp-profil__initiales" aria-hidden="true">{{ cpInitiales }}</span>
          <button type="button" class="cp-profil__edit" aria-label="Changer la photo de profil" @click="chooseNativePhoto">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8.5h3.2l1.3-2h7l1.3 2H20a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13.6" r="3.2"/></svg>
          </button>
        </span>
        <span class="cp-profil__texte">
          <span class="cp-profil__nom">{{ firstname }} {{ lastname }}</span>
          <span v-if="email" class="cp-profil__mail">{{ email }}</span>
        </span>
      </div>

      <form class="cp-form" @submit.prevent="submit">
        <div class="cp-champ">
          <label class="cp-champ__label" for="cp-prenom">Prénom</label>
          <input id="cp-prenom" v-model="firstname" type="text" class="cp-input" autocomplete="given-name">
        </div>
        <div class="cp-champ">
          <label class="cp-champ__label" for="cp-nom">Nom</label>
          <input id="cp-nom" v-model="lastname" type="text" class="cp-input" autocomplete="family-name">
        </div>
        <div class="cp-champ">
          <label class="cp-champ__label" for="cp-mail">Adresse e-mail</label>
          <input id="cp-mail" v-model="email" type="email" inputmode="email" class="cp-input" autocomplete="email">
        </div>
        <div class="cp-champ">
          <label class="cp-champ__label" for="cp-tel">Téléphone</label>
          <input id="cp-tel" v-model="phone" type="tel" inputmode="tel" class="cp-input" autocomplete="tel">
        </div>
        <div class="cp-champ">
          <label class="cp-champ__label" for="cp-naiss">Date de naissance</label>
          <input id="cp-naiss" v-model="birthdate" type="text" class="cp-input" placeholder="JJ/MM/AAAA" autocomplete="bday">
        </div>

        <button type="submit" class="cp-enregistrer">Enregistrer les modifications</button>
      </form>

      <div v-if="sports && sports.length" class="cp-prefs">
        <h2 class="cp-prefs__titre">Sports préférés</h2>
        <p class="cp-prefs__note">Pour des suggestions adaptées à vos envies.</p>
        <div class="cp-prefs__pastilles">
          <button
            v-for="sport in sports" :key="sport"
            type="button" class="cp-pref"
            :class="{ 'is-on': selectedSport === sport }"
            @click="selectedSport = selectedSport === sport ? null : sport"
          >{{ sport }}</button>
        </div>
      </div>

      <div v-if="types && types.length" class="cp-prefs">
        <h2 class="cp-prefs__titre">Types de sorties</h2>
        <p class="cp-prefs__note">Sessions, stages ou séjours : à vous de choisir.</p>
        <div class="cp-prefs__pastilles">
          <button
            v-for="t in types" :key="t"
            type="button" class="cp-pref"
            :class="{ 'is-on': selectedType === t }"
            @click="selectedType = selectedType === t ? null : t"
          >{{ t }}</button>
        </div>
      </div>
    </section>

    <!-- ---------- Pied d'écran ---------- -->
    <footer class="cp-pied">
      <nav class="cp-liens" aria-label="Informations légales">
        <RouterLink to="/CGU">CGU</RouterLink>
        <RouterLink to="/mentions-legales">Mentions légales</RouterLink>
        <RouterLink to="/politique-de-confidentialite">Confidentialité</RouterLink>
        <RouterLink to="/contact">Contact</RouterLink>
      </nav>
      <button type="button" class="cp-deco" @click="logout">Se déconnecter</button>
      <p class="cp-mentions">OuiCSport — SIREN 852 096 536 — Atout France IM095220001</p>
    </footer>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>

<section class="hero2">   
    </section>

    <div class="user-profile-container">
        <div class="profile-navv">
        <div class="profile-header">
            <div :class="['user-info-title']" @click="setActiveSection('info')">
                <h2 :class="['', { active: activeSection === 'info' }]">MES INFOS PERSONNELLES</h2>
            </div>
            <div :class="['user-reservations-title']" @click="setActiveSection('reservations')">
                <h2 :class="['', { active: activeSection === 'reservations' }]">MES RÉSERVATIONS</h2>
            </div>
        </div>
        <div class="user-content"  v-if="activeSection === 'info'">
              <div class="user-image" >
                <img :src="photoBackgroundStyle" alt="Profile Photo">
                <form class="upload-form">
                  <!-- Web : champ fichier historique, strictement inchangé.
                       Natif : même classe donc même overlay transparent, mais
                       ouvre le sélecteur photo du système. -->
                  <input
                    v-if="!isNative"
                    type="file"
                    ref="fileInput"
                    @change="handleFileChange"
                    class="upload-input"
                  />
                  <button
                    v-else
                    type="button"
                    class="upload-input"
                    aria-label="Changer la photo de profil"
                    @click="chooseNativePhoto"
                  ></button>
                  <input v-model="userId" type="hidden" />
                  <!-- Natif : le champ fichier est étalé en transparence sur
                       l'avatar, cette pastille en donne l'affordance. -->
                  <span v-if="isNative" class="oc-avatar-edit" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4 8.5h3.2l1.3-2h7l1.3 2H20a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z" />
                      <circle cx="12" cy="13.6" r="3.2" />
                    </svg>
                  </span>
                </form>
            </div>
            <div v-if="isNative" class="oc-account-id">
              <p class="oc-account-id__name">{{ firstname }} {{ lastname }}</p>
              <p class="oc-account-id__mail">{{ email }}</p>
              <div class="oc-account-id__credits">
                <span class="oc-account-id__credits-label">Crédits disponibles</span>
                <span class="oc-account-id__credits-value">{{ creditsBalance }} €</span>
              </div>
            </div>
            <div class="user-details">
            <h3 v-if="isNative" class="oc-account-section">Mes informations</h3>
            <div class="info-fields">
                <label v-if="isNative" class="oc-field__label" for="oc-profile-lastname">Nom</label>
                <input v-model="lastname" type="text" placeholder="Nom" id="oc-profile-lastname" class="oc-field__control">
                <label v-if="isNative" class="oc-field__label" for="oc-profile-firstname">Prénom</label>
                <input v-model="firstname" type="text" placeholder="Prénom" id="oc-profile-firstname" class="oc-field__control">
                <label v-if="isNative" class="oc-field__label" for="oc-profile-email">Email</label>
                <input v-model="email" type="email" placeholder="Email" id="oc-profile-email" class="oc-field__control">
                <label v-if="isNative" class="oc-field__label" for="oc-profile-birthdate">Date de naissance</label>
                <input v-model="birthdate" type="date" placeholder="Date de naissance" id="oc-profile-birthdate" class="oc-field__control">
                <label v-if="isNative" class="oc-field__label" for="oc-profile-phone">Téléphone</label>
                <input v-model="phone" type="tel" placeholder="Téléphone" id="oc-profile-phone" class="oc-field__control">
            </div>

            <button class="modify-info-btn" @click="updateProfile">Modifier mes infos</button>
            <p class="credits-display">
              Crédits disponibles : {{ creditsBalance }} €
            </p>

            <button class="credit-btn" @click="openStripeModal">
              Recharger mes crédits
            </button>
            <div class="logout-container">
              <button class="logout-btn" @click="logout">Déconnexion</button>
            </div>

        </div>
        </div>
      <div v-if="activeSection === 'reservations'">
        <h3 class="oc-account-section">À VENIR : </h3>

        <div v-if="upcomingOffers.length > 0" class="upcoming-activities">
          <div class="activities-grid">
  <div
    v-for="offer in upcomingOffers"
    :key="offer.id"
    class="activity oc-card"
    @click="navigateToOfferDetails(offer.id)"
    :class="{ canceled: offer.status === 'CANCELED' }"
  >


    <!-- Image -->
    <div v-for="photo in offer.photos" :key="photo.imageName">
      <img :src="photo.imageUrl" :alt="offer.sport">
    </div>

    <!-- Organisation -->
    <div class="activity-orga oc-badge" :class="{ 'oc-badge--danger': offer.status === 'CANCELED' }">
      <p v-if="offer.status === 'CANCELED'">Annulé</p>
      <p v-else>ORGA OUICSPORT</p>
    </div>

    <!-- Type -->
    <div class="activity-type">
      <p>{{ offer.type }}</p>
    </div>

    <!-- Date -->
    <div class="activity-date" style="font-size: clamp(1rem, 2vw, 1.4rem); font-weight: bold;">
      <p>{{ offer.formattedDate }}</p>
    </div>

    <!-- Infos -->
    <div class="activity-info">
      <p class="activity-name">{{ offer.titleOffers }}</p>
      <p class="activity-session">{{ offer.sport }}</p>
      <p class="activity-place">{{ offer.address }} {{ offer.city }}, {{ offer.postcode }}, {{ offer.country }}</p>
      <p class="activity-place">Nombre de place : {{ offer.quantity }}</p>
      <p class="activity-price" :class="{ canceled: offer.status === 'CANCELED' }">
        {{ offer.amount }}€ ({{ offer.quantity }})
      </p>
    </div>
  </div>
</div>

</div>

<!-- Natif : la liste vide restait un titre suivi de rien du tout. -->
<div v-if="isNative && upcomingOffers.length === 0" class="oc-empty">
  <p class="oc-empty__title">Aucune réservation à venir</p>
  <p class="oc-empty__text">Vos prochaines activités s'afficheront ici dès que vous en aurez réservé une.</p>
</div>

<div v-if="pastOffers.length > 0" class="past-activities">
  <h3 class="oc-account-section">PASSÉES : </h3>
  <div class="activities-grid">
    <div
      v-for="offer in pastOffers"
      :key="offer.id"
      class="activity oc-card"
      @click="navigateToOfferDetails(offer.id)"
    >
      <div v-for="photo in offer.photos" :key="photo.imageName">
        <img :src="photo.imageUrl" :alt="offer.sport">
      </div>
      <div class="activity-orga oc-badge" :class="{ 'oc-badge--danger': offer.status === 'CANCELED' }">
        <p v-if="offer.status === 'CANCELED'">Annulé</p>
        <p v-else>ORGA OUICSPORT</p>
      </div>
      <div class="activity-type">
        <p>{{ offer.type }}</p>
      </div>
      <div class="activity-date" style="font-size: clamp(1rem, 2vw, 1.4rem);
    font-weight: bold;">
        <p>{{ offer.formattedDate }}</p>
      </div>
      <div class="activity-info">
        <p class="activity-name">{{ offer.titleOffers }}</p>
        <p class="activity-session">{{ offer.sport }}</p>
        <p class="activity-place">{{ offer.address }} {{ offer.city }}, {{ offer.postcode }}, {{ offer.country }}</p>
        <p class="activity-price">{{ offer.amount }}€ ({{ offer.quantity }})</p>
      </div>
    </div>
  </div>
</div>

        </div>
    </div>
  </div>
  <div v-if="showModal" class="modal oc-account-sheet" style="display: block;">
  <div class="modal-content">
    <div v-if="isNative" class="oc-sheet-handle" aria-hidden="true"></div>
    <span class="close" @click="closeModal">&times;</span>
    <h2 :class="{ canceled: selectedOffer.status === 'CANCELED' }">
      {{ selectedOffer.sport }}
    </h2>
    <div v-for="photo in selectedOffer.photos" :key="photo.imageName">
      <img :src="photo.imageUrl" :alt="selectedOffer.sport" 
           :style="{ filter: selectedOffer.status === 'CANCELED' ? 'grayscale(100%)' : 'none' }">
    </div>
    <p><strong>Date :</strong> {{ selectedOffer.formattedDate }}</p>
    <p><strong>Club :</strong> <span v-html="decodeHtml(selectedOffer.clubName)"></span></p>
    <p><strong>Adresse :</strong> {{ selectedOffer.address }} {{ selectedOffer.city }}, {{ selectedOffer.postcode }}, {{ selectedOffer.country }}</p>
    <p><strong>Nombre de {{ selectedOffer.quantity > 1 ? 'places' : 'place' }} :</strong> {{ selectedOffer.quantity }}</p>
    <br>

    <div v-if="selectedOffer.selectedEquipments && selectedOffer.selectedEquipments.length > 0">
      <h3>Équipements sélectionnés :</h3>
      <ul>
        <li v-for="(equipment, index) in selectedOffer.selectedEquipments" :key="index">
          {{ equipment.name }} - Quantité: {{ equipment.quantity }} - Prix: {{ equipment.price }} €
        </li>
      </ul>
    </div>

    <div 
      class="price-total" 
      :class="{ canceled: selectedOffer.status === 'CANCELED' }"
      style="text-align: center; font-size: 1.5em; margin-bottom: 3%; font-weight: bold; border-top: 1px solid #ccc; padding-top: 10px; margin-top: 20px;">
      Prix total : {{ selectedOffer.amount }} €
    </div>

    <div class="modal-buttons">
      <button 
        class="btn pay" 
        @click="openConfirmationModal" 
        v-if="!isOfferPassed(selectedOffer) && selectedOffer.status !== 'CANCELED'">
        Annulation
      </button>
      <RouterLink
        v-if="!isOfferPassed(selectedOffer)"
        :to="{ name: 'OfferDetailsPage', params: { id: selectedOffer.ID_Criteria } }"
        class="btn pay"
      >
        Voir les détails de l'offre
      </RouterLink>
    </div>
  </div>
</div>


<div v-if="showConfirmationModal" class="modal oc-account-sheet" style="display: block;">
  <div class="modal-content">
    <div v-if="isNative" class="oc-sheet-handle" aria-hidden="true"></div>
    <p>{{ confirmationMessage }}</p>
    <div style="display: flex; justify-content: space-between; gap: 10px;">
      <button v-if="showConfirmationButtons" @click="confirmCancellation" class="btn confirm" style="margin-right: 10px;">Oui</button>
      <button v-if="showConfirmationButtons" @click="closeConfirmationModal" class="btn cancel">Non</button>
      <button v-else @click="closeConfirmationModal" class="btn ok">Ok</button>
    </div>
  </div>
</div>

<div v-if="showStripeModal" class="modal oc-account-sheet" style="display: block;">
  <div class="modal-content">
    <div v-if="isNative" class="oc-sheet-handle" aria-hidden="true"></div>
    <span class="close" @click="closeStripeModal">&times;</span>
    <h2>Recharger mes crédits</h2>

    <label for="creditAmount" class="oc-field__label">Montant à recharger (en €) :</label>
    <input
      id="creditAmount"
      v-model.number="creditAmount"
      type="number"
      placeholder="Ex : 10"
      min="1"
      class="oc-field__control"
      style="margin-bottom: 10px;"
    />

    <button class="btn continue" v-if="!stripeReady" @click="handleStripeSetup">
      Continuer
    </button>

    <div v-if="stripeReady">
      <div id="card-element" class="stripe-element" style="margin-top: 20px;"></div>
      <p v-if="cardErrors" class="error-message">{{ cardErrors }}</p>

      <button class="btn pay" @click="payWithCard">
        Payer
      </button>
    </div>
  </div>
</div>



  <br><br><br><br>

   


  </template>
</template>
<style scoped>
.credits-display {
  font-size: 1.2rem;
  font-weight: bold;
  color: #2c3e50;
  margin-top: 10px;
  margin-bottom: 5px;
}

.credit-btn {
  background-color: #27ae60;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  transition: background-color 0.3s ease;
  margin-top: 2px;

}



.credit-btn:hover {
  background-color: #1e8449;
}

.modal-content {
 background-color: #fff; /* Couleur de fond */
 padding: 20px; /* Espace intérieur */
 border-radius: 8px; /* Bords arrondis */
 box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Ombre portée */
 width: 80%; /* Largeur du modal */
 max-width: 600px; /* Largeur maximale */
 max-height: 70vh; /* Hauteur maximale */
 overflow-y: auto;
}

.close {
 color: #aaa; /* Couleur du bouton de fermeture */
 float: right; /* Positionnement à droite */
 font-size: 28px; /* Taille de la police */
 cursor: pointer; /* Curseur pointeur */
}

.close:hover {
 color: #333; /* Couleur au survol */
}


.modal-content h2 {
  color: #333; /* Couleur du titre */
  margin-bottom: 20px; /* Espace après le titre */
}

.modal-content img {
  max-width: 100%; /* S'assure que l'image ne dépasse pas du modal */
  height: auto; /* Garde les proportions de l'image */
  margin-bottom: 20px; /* Espace après l'image */
}

.modal-content p {
  color: #555; /* Couleur du texte */
  line-height: 1.5; /* Hauteur de ligne pour améliorer la lisibilité */
  margin-bottom: 10px; /* Espace entre les paragraphes */
}

.modal-content ul {
 list-style: none; /* Suppression des puces */
 padding: 0; /* Suppression du padding */
 margin: 0; /* Suppression de la marge */
 margin-bottom: 20px; 
}

.modal-content li {
 padding: 10px; /* Espace intérieur des éléments de liste */
 border-bottom: 1px solid #eee; /* Bordure inférieure */
 cursor: pointer; /* Curseur pointeur */
}

.modal-content li:hover {
 background-color: #f8f8f8; /* Couleur de fond au survol */
}

/* Styles pour les barres de défilement (optionnel) */
.modal-content::-webkit-scrollbar {
 width: 10px; /* Largeur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-track {
 background: #f1f1f1; /* Couleur de fond de la piste */
}

.modal-content::-webkit-scrollbar-thumb {
 background: #888; /* Couleur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-thumb:hover {
 background: #555; /* Couleur de la barre de défilement au survol */
}

/* Adaptation pour les petits écrans */
@media (max-width: 600px) {
 .modal-content {
     width: 95%; /* Largeur plus grande pour les petits écrans */
     margin: 5% auto; /* Marge verticale réduite */
 }
}
.logout-container {
  display: flex;              /* Utilise Flexbox */
  justify-content: center;     /* Centre horizontalement */
  align-items: center;         /* Centre verticalement */
  width: 100%;                 /* Largeur 100% */
  margin-top: 30px;
}

/* Style du bouton */
.logout-btn {
  background-color: #f44336;   /* Rouge pour indiquer une action importante */
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-transform: uppercase;
}

.logout-btn:hover {
  background-color: #d32f2f;   /* Rouge plus foncé au survol */
}


.upload-form {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 20px;
}

.upload-input {
  display: inline-block;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px; /* Taille de texte réduite */
  font-weight: 500; /* Épaisseur du texte plus légère */
  text-transform: uppercase;
  transition: background-color 0.3s ease;
}

.upload-input:hover {
  background-color: #c0392b; /* Couleur plus foncée au survol */
}

/* Style pour le bouton de sélection de fichier */
.upload-input[type="file"]::file-selector-button {
  background-color: #e74c3c;
  color: white;
  padding: 8px 12px;
  border: none;
  border-radius: 5px;
  font-size: 14px; /* Taille de texte plus petite */
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

/* Effet au survol du bouton */
.upload-input[type="file"]::file-selector-button:hover {
  background-color: #c0392b;
}

.error-message {
  color: #e85746;
  margin-top: 10px;
  font-weight: bold;
}

</style>

<style>
/* =========================================================================
   Écran COMPTE — rendu applicatif natif (iOS / Android)
   -------------------------------------------------------------------------
   Toutes les règles sont préfixées `body.is-native` : sur www.ouicsport.fr la
   classe n'existe pas, le rendu web est donc strictement inchangé.

   Ce que corrige cette passe :
   - Le fichier mélangeait trois échelles de gris (#f2f2f7 iOS, #6b7280 et
     #d1d5db Tailwind, #333 web) et deux rouges. Tout passe sur les neutres
     --oc-ink* et l'unique --oc-brand.
   - Les onglets « Mes infos / Mes réservations » héritaient de pastilles
     blanches ombrées du site ; ils deviennent un sélecteur segmenté.
   - Les réservations deviennent des cartes .oc-card avec un badge d'état,
     au lieu d'une grille à trois colonnes repliée de force.
   - Les modales étaient déclarées en feuille basse mais gardaient le
     `position:absolute; top:50%; transform:translate(-50%,-50%)` du site :
     elles s'affichaient centrées et rognées. Ce positionnement est neutralisé.
   ========================================================================= */

/* -------------------------------------------------------------------------
   1. Gabarit de l'écran
   ------------------------------------------------------------------------- */

/* Bandeau d'image plein cadre en haut de page : motif de site, sans objet
   dans une app où la barre d'application tient déjà ce rôle. */
body.is-native .hero2 {
  display: none;
}

/* Quatre <br> de calage en fin de composant : ils creusaient un trou avant
   le bloc « Informations ». Le padding de .page-main suffit. */
body.is-native .user-profile-container ~ br {
  display: none;
}

body.is-native .user-profile-container {
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background: var(--oc-bg);
  box-sizing: border-box;
}

body.is-native .profile-navv {
  width: 100%;
  background: var(--oc-bg);
}

/* -------------------------------------------------------------------------
   2. Sélecteur segmenté : Mes infos / Mes réservations
   ------------------------------------------------------------------------- */

body.is-native .profile-header {
  position: sticky;
  /* La barre d'application est elle-même sticky à 0 : on se cale dessous. */
  top: calc(var(--oc-header-h) + var(--oc-safe-top));
  z-index: 20;
  display: flex;
  gap: var(--oc-sp-2);
  width: 100%;
  margin: 0;
  padding: var(--oc-sp-3) var(--oc-sp-4);
  background: var(--oc-bg);
  box-sizing: border-box;
}

body.is-native .profile-header > div {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  padding: 0;
  text-align: center;
}

body.is-native .profile-header h2 {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--oc-tap);
  margin: 0;
  padding: 0 var(--oc-sp-2);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.2;
  color: var(--oc-ink-2);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-full);
  box-shadow: none;
  transition: color var(--oc-fast) var(--oc-ease),
              background var(--oc-fast) var(--oc-ease);
}

/* Le site force `background-color: #fdf3f3 !important` sur l'onglet actif :
   seul un !important de spécificité supérieure peut le neutraliser. */
body.is-native .profile-header h2.active {
  color: var(--oc-brand-ink);
  background: var(--oc-brand) !important;
  box-shadow: var(--oc-e1);
}

/* Titre de rubrique, commun aux trois sections de l'écran. */
body.is-native .oc-account-section {
  margin: 0 0 var(--oc-sp-3);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--oc-ink);
}

/* -------------------------------------------------------------------------
   3. Onglet « Mes infos »
   ------------------------------------------------------------------------- */

body.is-native .user-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
  padding: var(--oc-sp-2) var(--oc-sp-4) var(--oc-sp-6);
  border-radius: 0;
  box-sizing: border-box;
}

/* --- En-tête de profil : avatar rond, identité, crédits ------------------ */

body.is-native .user-image {
  position: relative;
  flex: 0 0 auto;
  align-self: center;
  width: 112px;
  height: 112px;
  margin: var(--oc-sp-2) 0 var(--oc-sp-4);
}

body.is-native .user-image img {
  width: 100%;
  height: 100%;
  border-radius: var(--oc-r-full);
  object-fit: cover;
  background: var(--oc-surface-2);
  border: 3px solid var(--oc-surface);
  box-shadow: var(--oc-e2);
  box-sizing: border-box;
}

/* Le champ fichier natif affiche un bouton système et le nom du fichier :
   illisible et impossible à traduire. On l'étale en transparence sur
   l'avatar — toucher la photo ouvre la galerie, comme partout ailleurs. */
body.is-native .user-image .upload-form {
  position: absolute;
  inset: 0;
  display: block;
  margin: 0;
  border-radius: var(--oc-r-full);
}

body.is-native .user-image .upload-input,
body.is-native .user-image .upload-input:hover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  background: transparent;
  opacity: 0;
  cursor: pointer;
}

body.is-native .oc-avatar-edit {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
  border: 2px solid var(--oc-bg);
  border-radius: var(--oc-r-full);
  box-shadow: var(--oc-e1);
  pointer-events: none; /* le geste appartient au champ fichier dessous */
}

body.is-native .oc-avatar-edit svg {
  display: block;
  width: 17px;
  height: 17px;
}

body.is-native .oc-account-id {
  margin: 0 0 var(--oc-sp-6);
  text-align: center;
}

body.is-native .oc-account-id__name {
  margin: 0;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.3px;
  color: var(--oc-ink);
  overflow-wrap: anywhere;
}

body.is-native .oc-account-id__mail {
  margin: var(--oc-sp-1) 0 0;
  font-size: var(--oc-text-sm);
  color: var(--oc-ink-3);
  overflow-wrap: anywhere;
}

/* Le solde était une ligne de texte perdue entre deux boutons. Il devient
   l'élément dominant de l'en-tête, seul usage du rouge à cet endroit. */
body.is-native .oc-account-id__credits {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oc-sp-3);
  margin-top: var(--oc-sp-4);
  padding: var(--oc-sp-3) var(--oc-sp-4);
  background: var(--oc-brand-50);
  border-radius: var(--oc-r-lg);
  text-align: left;
}

body.is-native .oc-account-id__credits-label {
  font-size: var(--oc-text-sm);
  font-weight: 700;
  color: var(--oc-brand-700);
}

body.is-native .oc-account-id__credits-value {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  line-height: 1;
  color: var(--oc-brand);
  white-space: nowrap;
}

/* --- Rubrique « Mes informations » --------------------------------------- */

body.is-native .user-details {
  display: block;
  flex: 0 0 auto;
  width: 100%;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  box-sizing: border-box;
}

body.is-native .info-fields {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
  box-sizing: border-box;
}

/* Les libellés ne sont rendus qu'en natif : on reconstitue l'espacement de
   .oc-field sans encapsuler les champs (le markup web doit rester intact). */
body.is-native .info-fields .oc-field__label {
  margin-top: var(--oc-sp-4);
  margin-bottom: 6px;
}

body.is-native .info-fields .oc-field__label:first-child {
  margin-top: 0;
}

body.is-native .info-fields input {
  width: 100%;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-4);
  /* 16px : en dessous, iOS zoome tout seul à la mise au point du champ. */
  font-size: var(--oc-text-base);
  line-height: normal;
  color: var(--oc-ink);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  outline: none;
  transition: border-color var(--oc-fast) var(--oc-ease),
              box-shadow var(--oc-fast) var(--oc-ease);
}

body.is-native .info-fields input:focus {
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

/* --- Actions du compte --------------------------------------------------- */

body.is-native .modify-info-btn,
body.is-native .modify-info-btn:hover {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--oc-tap);
  margin-top: var(--oc-sp-4);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
  border: 0;
  border-radius: var(--oc-r-full);
  box-shadow: var(--oc-e1);
  box-sizing: border-box;
  cursor: pointer;
  transition: transform var(--oc-fast) var(--oc-ease),
              background var(--oc-fast) var(--oc-ease);
}

body.is-native .modify-info-btn:active {
  background: var(--oc-brand-600);
  transform: scale(0.985);
}

/* Le solde est désormais mis en avant dans l'en-tête de profil : le répéter
   ici en petit texte gris dupliquait l'information sans la hiérarchiser. */
body.is-native .credits-display {
  display: none;
}

body.is-native .credit-btn,
body.is-native .credit-btn:hover {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--oc-tap);
  margin-top: var(--oc-sp-3);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--oc-brand-700);
  background: var(--oc-brand-50);
  border: 0;
  border-radius: var(--oc-r-full);
  box-sizing: border-box;
  cursor: pointer;
  transition: transform var(--oc-fast) var(--oc-ease);
}

body.is-native .credit-btn:active {
  transform: scale(0.985);
}

body.is-native .logout-container {
  display: block;
  width: 100%;
  margin-top: var(--oc-sp-8);
}

/* La déconnexion est une action destructrice et rare : elle ne doit pas
   porter le même rouge plein que l'action principale de l'écran. */
body.is-native .logout-btn,
body.is-native .logout-btn:hover {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--oc-danger);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line);
  border-radius: var(--oc-r-full);
  box-sizing: border-box;
  cursor: pointer;
}

body.is-native .logout-btn:active {
  background: var(--oc-danger-bg);
}

/* -------------------------------------------------------------------------
   4. Onglet « Mes réservations »
   -------------------------------------------------------------------------
   Les sélecteurs sont ancrés sur .profile-navv : les classes .activity*
   sont partagées avec la liste d'offres, qui a sa propre mise en forme. */

body.is-native .profile-navv > div > h3.oc-account-section {
  margin: var(--oc-sp-5) var(--oc-sp-4) var(--oc-sp-3);
}

body.is-native .profile-navv .past-activities > h3.oc-account-section {
  margin: var(--oc-sp-8) 0 var(--oc-sp-3);
}

body.is-native .profile-navv .upcoming-activities,
body.is-native .profile-navv .past-activities {
  width: 100%;
  padding: 0 var(--oc-sp-4);
  text-align: left;
  box-sizing: border-box;
}

body.is-native .profile-navv .activities-grid {
  display: flex;
  flex-direction: column;
  gap: var(--oc-sp-4);
  width: 100%;
  padding: 0;
}

body.is-native .profile-navv .activity {
  position: relative;
  width: 100%;
  padding: 0;
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
  overflow: hidden;
  box-sizing: border-box;
  transition: transform var(--oc-fast) var(--oc-ease);
}

body.is-native .profile-navv .activity:active {
  transform: scale(0.99);
}

/* Toutes les photos de l'offre étaient empilées : une carte de réservation
   pouvait faire cinq écrans de haut. On n'en garde qu'une. */
body.is-native .profile-navv .activity > div:not([class]) {
  display: none;
}

body.is-native .profile-navv .activity > div:not([class]):first-child {
  display: block;
}

body.is-native .profile-navv .activity img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 0;
  background: var(--oc-surface-2);
}

/* Badge d'état, posé sur la photo. Les couleurs viennent de .oc-badge /
   .oc-badge--danger : ici on ne règle que l'ancrage. */
body.is-native .profile-navv .activity .activity-orga {
  top: var(--oc-sp-3);
  right: var(--oc-sp-3);
  box-shadow: var(--oc-e1);
}

body.is-native .profile-navv .activity .activity-orga p {
  margin: 0;
}

body.is-native .profile-navv .activity .activity-type {
  top: var(--oc-sp-3);
  left: var(--oc-sp-3);
  padding: 3px var(--oc-sp-2);
  font-size: var(--oc-text-xs);
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #FFFFFF;
  background: rgba(26, 22, 20, 0.62);
  border-radius: var(--oc-r-sm);
}

body.is-native .profile-navv .activity .activity-type p {
  margin: 0;
}

body.is-native .profile-navv .activity .activity-date {
  display: inline-block;
  margin: var(--oc-sp-4) var(--oc-sp-4) 0;
  padding: var(--oc-sp-1) var(--oc-sp-3);
  color: var(--oc-brand-700);
  background: var(--oc-brand-50);
  border-radius: var(--oc-r-full);
  text-align: left;
}

body.is-native .profile-navv .activity .activity-date p {
  margin: 0;
  font-size: var(--oc-text-sm);
  font-weight: 700;
}

body.is-native .profile-navv .activity .activity-info {
  padding: var(--oc-sp-3) var(--oc-sp-4) var(--oc-sp-4);
  background: transparent;
}

body.is-native .profile-navv .activity .activity-name {
  margin: 0 0 var(--oc-sp-1);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  line-height: 1.25;
  color: var(--oc-ink);
}

body.is-native .profile-navv .activity .activity-session {
  margin: 0 0 var(--oc-sp-1);
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-ink-2);
}

/* L'audit relevait ici du #222 sur #333 : contraste 1,29:1. */
body.is-native .profile-navv .activity .activity-place {
  margin: 0 0 2px;
  font-size: var(--oc-text-sm);
  font-weight: 400;
  line-height: 1.45;
  color: var(--oc-ink-3);
}

body.is-native .profile-navv .activity .activity-price {
  display: block;
  margin: var(--oc-sp-3) 0 0;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  color: var(--oc-brand);
}

body.is-native .profile-navv .activity .activity-price.canceled {
  color: var(--oc-ink-3);
  text-decoration: line-through;
}

/* -------------------------------------------------------------------------
   5. Modales en feuille basse
   -------------------------------------------------------------------------
   Ancrées sur .oc-account-sheet pour ne pas déborder sur les modales des
   autres écrans, qui partagent les classes .modal / .modal-content. */

body.is-native .oc-account-sheet {
  /* Le markup porte un style inline `display: block` : il faut un !important. */
  display: flex !important;
  position: fixed;
  inset: 0;
  z-index: 1000;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(26, 22, 20, 0.55);
  overflow: hidden;
}

body.is-native .oc-account-sheet .modal-content {
  /* Le site centre la modale en absolu ; en feuille basse cela la rognait. */
  position: relative;
  top: auto;
  left: auto;
  transform: none;
  width: 100%;
  max-width: 100%;
  max-height: 86vh;
  margin: 0;
  padding: var(--oc-sp-4) var(--oc-sp-4) calc(var(--oc-sp-6) + var(--oc-safe-bottom));
  background: var(--oc-surface);
  border-radius: var(--oc-r-xl) var(--oc-r-xl) 0 0;
  box-shadow: var(--oc-e3);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

body.is-native .oc-sheet-handle {
  width: 40px;
  height: 4px;
  margin: 0 auto var(--oc-sp-4);
  background: var(--oc-line-2);
  border-radius: var(--oc-r-full);
}

body.is-native .oc-account-sheet .modal-content .close {
  position: absolute;
  top: var(--oc-sp-3);
  right: var(--oc-sp-3);
  z-index: 2;
  float: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  font-size: 24px;
  line-height: 1;
  color: var(--oc-ink-2);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-full);
}

body.is-native .oc-account-sheet .modal-content h2 {
  margin: 0 0 var(--oc-sp-4);
  padding-right: var(--oc-tap);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  line-height: 1.2;
  color: var(--oc-ink);
}

body.is-native .oc-account-sheet .modal-content h2.canceled {
  color: var(--oc-danger);
}

body.is-native .oc-account-sheet .modal-content h3 {
  margin: var(--oc-sp-4) 0 var(--oc-sp-2);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  color: var(--oc-ink);
}

body.is-native .oc-account-sheet .modal-content p {
  margin: 0 0 var(--oc-sp-2);
  font-size: var(--oc-text-base);
  line-height: 1.5;
  color: var(--oc-ink-2);
}

body.is-native .oc-account-sheet .modal-content p strong {
  font-weight: 700;
  color: var(--oc-ink);
}

body.is-native .oc-account-sheet .modal-content img {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  margin-bottom: var(--oc-sp-4);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-md);
}

body.is-native .oc-account-sheet .modal-content ul {
  margin: 0 0 var(--oc-sp-4);
  padding: 0;
  list-style: none;
}

body.is-native .oc-account-sheet .modal-content li,
body.is-native .oc-account-sheet .modal-content li:hover {
  padding: var(--oc-sp-3) 0;
  font-size: var(--oc-text-sm);
  color: var(--oc-ink-2);
  background: transparent;
  border-bottom: 1px solid var(--oc-line);
}

/* Total : le style inline impose la taille, on ne reprend que la couleur. */
body.is-native .oc-account-sheet .price-total {
  font-family: var(--oc-font-display);
  color: var(--oc-brand);
  border-top-color: var(--oc-line) !important;
}

body.is-native .oc-account-sheet .price-total.canceled {
  color: var(--oc-ink-3);
}

body.is-native .oc-account-sheet .modal-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--oc-sp-2);
  margin-top: var(--oc-sp-5);
}

body.is-native .oc-account-sheet .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.2px;
  text-align: center;
  text-decoration: none;
  border: 0;
  border-radius: var(--oc-r-full);
  box-sizing: border-box;
  cursor: pointer;
  transition: transform var(--oc-fast) var(--oc-ease);
}

body.is-native .oc-account-sheet .btn:active {
  transform: scale(0.985);
}

body.is-native .oc-account-sheet .btn.pay,
body.is-native .oc-account-sheet .btn.confirm,
body.is-native .oc-account-sheet .btn.continue,
body.is-native .oc-account-sheet .btn.ok {
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
  box-shadow: var(--oc-e1);
}

body.is-native .oc-account-sheet .btn.cancel {
  color: var(--oc-ink-2);
  background: var(--oc-surface-2);
}

/* --- Feuille de rechargement de crédits ---------------------------------- */

body.is-native .oc-account-sheet .modal-content label {
  display: block;
  margin-bottom: 6px;
  font-size: var(--oc-text-sm);
  font-weight: 700;
  color: var(--oc-ink-2);
}

body.is-native .oc-account-sheet .modal-content input[type="number"] {
  width: 100%;
  min-height: var(--oc-tap);
  /* Le style inline pose margin-bottom: 10px. */
  margin-bottom: var(--oc-sp-4) !important;
  padding: 0 var(--oc-sp-4);
  font-size: var(--oc-text-base);
  color: var(--oc-ink);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  outline: none;
}

body.is-native .oc-account-sheet .modal-content input[type="number"]:focus {
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

body.is-native .oc-account-sheet .stripe-element {
  padding: var(--oc-sp-3) var(--oc-sp-4);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  margin-bottom: var(--oc-sp-4);
}

body.is-native .oc-account-sheet .error-message {
  margin: var(--oc-sp-2) 0 var(--oc-sp-3);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  color: var(--oc-danger);
}

/* =========================================================================
   MON COMPTE — feuille de l'application, écrite de zéro
   =========================================================================
   Balisage natif distinct de celui du site : rien n'est hérité ni combattu.
   Sélecteurs préfixés `body.is-native` pour que le contrôle automatique
   vérifie que le site reste intact.
   ========================================================================= */

body.is-native .cp-page,
body.is-native .cp-page *,
body.is-native .cp-page *::before,
body.is-native .cp-page *::after {
  box-sizing: border-box;
}

body.is-native .cp-page header,
body.is-native .cp-page footer,
body.is-native .cp-page section,
body.is-native .cp-page nav,
body.is-native .cp-page h1,
body.is-native .cp-page h2,
body.is-native .cp-page p,
body.is-native .cp-page ul,
body.is-native .cp-page li,
body.is-native .cp-page span,
body.is-native .cp-page div,
body.is-native .cp-page form,
body.is-native .cp-page label,
body.is-native .cp-page button,
body.is-native .cp-page a,
body.is-native .cp-page img,
body.is-native .cp-page strong {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .cp-page {
  --cp-bg: #F5F0E6;
  --cp-surface: #EFE8DA;
  --cp-raised: #E5DDD0;
  --cp-dark: #2B231F;
  --cp-ink: #28201C;
  --cp-ink-2: #6B5E54;
  --cp-ink-3: #8C7E73;
  --cp-on-dark: #FAF6EE;
  --cp-red: #DD4124;
  --cp-vert: #2E7D32;
  --cp-hair: rgba(107, 94, 84, 0.14);

  min-height: 100%;
  padding: calc(env(safe-area-inset-top, 0px) + 14px) 16px var(--oc-sp-6);
  background: var(--cp-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--cp-ink);
  -webkit-user-select: none;
  user-select: none;
}

body.is-native .cp-page .cp-top { padding-bottom: 14px; }

body.is-native .cp-page .cp-top__title {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 28px;
  line-height: 1;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--cp-ink);
}

/* ---------- Solde de crédits ---------- */

body.is-native .cp-page .cp-solde {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  overflow: hidden;
  padding: 18px 20px;
  margin-bottom: 20px;
  color: var(--cp-on-dark);
  background: var(--cp-dark);
  border: 1px solid rgba(250, 246, 238, 0.12);
  border-radius: 22px;
  box-shadow: 0 8px 24px rgba(43, 35, 31, 0.22);
}

body.is-native .cp-page .cp-solde__bulle {
  position: absolute;
  top: -30px;
  right: -30px;
  width: 110px;
  height: 110px;
  background: var(--cp-red);
  border-radius: 55px;
  opacity: 0.18;
  pointer-events: none;
}

body.is-native .cp-page .cp-solde__gauche {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .cp-page .cp-solde__label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--cp-ink-3);
}

body.is-native .cp-page .cp-solde__montant {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 34px;
  line-height: 1.05;
  color: var(--cp-on-dark);
  font-variant-numeric: tabular-nums;
}

body.is-native .cp-page .cp-solde__montant span {
  font-size: 20px;
  color: var(--cp-red);
}

body.is-native .cp-page .cp-solde__note {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(250, 246, 238, 0.75);
}

body.is-native .cp-page .cp-solde__ajout {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 40px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 800;
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border-radius: 20px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(221, 65, 36, 0.35);
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-solde__ajout:active { transform: scale(0.95); }

/* ---------- Onglets ---------- */

body.is-native .cp-page .cp-onglets {
  position: relative;
  display: flex;
  margin-bottom: 18px;
  padding: 4px;
  background: var(--cp-surface);
  border: 1px solid rgba(107, 94, 84, 0.15);
  border-radius: 16px;
}

body.is-native .cp-page .cp-onglets__marque {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  background: var(--cp-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(40, 32, 28, 0.12);
  transition: transform 250ms cubic-bezier(0.2, 0, 0, 1);
}

body.is-native .cp-page .cp-onglets__marque.is-right { transform: translateX(100%); }

body.is-native .cp-page .cp-onglet {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  height: 42px;
  font-size: 15px;
  font-weight: 800;
  color: var(--cp-ink-2);
  border-radius: 12px;
  cursor: pointer;
  transition: color 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-onglet.is-on { color: var(--cp-ink); }

body.is-native .cp-page .cp-onglet__nb {
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 800;
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border-radius: 8px;
}

/* ---------- Réservations ---------- */

body.is-native .cp-page .cp-titre {
  margin-bottom: 12px;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 19px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--cp-ink);
}

body.is-native .cp-page .cp-titre--espace { margin-top: 26px; }

body.is-native .cp-page .cp-resas {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

body.is-native .cp-page .cp-resa {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--cp-surface);
  border: 1px solid var(--cp-hair);
  border-radius: 20px;
}

body.is-native .cp-page .cp-resa.is-annulee { opacity: 0.72; }
body.is-native .cp-page .cp-resa.is-passee { opacity: 0.82; }

body.is-native .cp-page .cp-resa__haut {
  display: flex;
  min-height: 120px;
  cursor: pointer;
}

/* Vignette bridée à 115px : les photos de réservation plafonnent à 414px de
   large côté serveur, elles restent donc nettes en densité double. */
body.is-native .cp-page .cp-resa__vignette {
  position: relative;
  flex: 0 0 115px;
  width: 115px;
  overflow: hidden;
  background: var(--cp-raised);
}

body.is-native .cp-page .cp-resa__vignette img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

body.is-native .cp-page .cp-resa__type {
  position: absolute;
  top: 6px;
  left: 6px;
  max-width: 103px;
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--cp-on-dark);
  background: rgba(43, 35, 31, 0.88);
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .cp-page .cp-resa__corps {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 12px;
}

body.is-native .cp-page .cp-resa__titre {
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  color: var(--cp-ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

body.is-native .cp-page .cp-resa__ligne {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 2px;
  font-size: 13px;
  font-weight: 600;
  color: var(--cp-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .cp-page .cp-resa__ligne svg { flex: 0 0 auto; }

body.is-native .cp-page .cp-resa__etat {
  align-self: flex-start;
  margin-top: auto;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 800;
  border-radius: 8px;
}

body.is-native .cp-page .cp-resa__etat.is-ok {
  color: var(--cp-vert);
  background: rgba(46, 125, 50, 0.12);
}

body.is-native .cp-page .cp-resa__etat.is-ko {
  color: var(--cp-red);
  background: rgba(221, 65, 36, 0.12);
}

body.is-native .cp-page .cp-resa__bas {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: var(--cp-raised);
  border-top: 1px solid rgba(107, 94, 84, 0.1);
}

body.is-native .cp-page .cp-resa__prix {
  font-size: 14px;
  font-weight: 700;
  color: var(--cp-ink-2);
}

body.is-native .cp-page .cp-resa__prix strong {
  font-size: 16px;
  font-weight: 800;
  color: var(--cp-ink);
  font-variant-numeric: tabular-nums;
}

/* L'annulation est destructrice : elle reste en contour, jamais en aplat
   plein, pour ne pas se confondre avec une action positive. */
body.is-native .cp-page .cp-resa__annuler {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 36px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 800;
  color: var(--cp-red);
  border: 1.5px solid var(--cp-red);
  border-radius: 18px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-resa__annuler:active {
  color: var(--cp-on-dark);
  background: var(--cp-red);
}

/* ---------- Aucune réservation ---------- */

body.is-native .cp-page .cp-vide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 34px 20px;
  text-align: center;
  background: var(--cp-surface);
  border: 1px dashed rgba(107, 94, 84, 0.28);
  border-radius: 20px;
}

body.is-native .cp-page .cp-vide__rond {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  color: var(--cp-red);
  background: var(--cp-raised);
  border-radius: 32px;
}

body.is-native .cp-page .cp-vide__titre {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 21px;
  line-height: 1.1;
  text-transform: uppercase;
  color: var(--cp-ink);
}

body.is-native .cp-page .cp-vide__texte {
  max-width: 32ch;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--cp-ink-2);
}

body.is-native .cp-page .cp-vide__cta {
  display: flex;
  align-items: center;
  min-height: 44px;
  margin-top: 4px;
  padding: 0 20px;
  font-size: 15px;
  font-weight: 800;
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border-radius: 22px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(221, 65, 36, 0.3);
}

/* ---------- Profil ---------- */

body.is-native .cp-page .cp-profil {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding: 16px;
  background: var(--cp-surface);
  border: 1px solid var(--cp-hair);
  border-radius: 20px;
}

body.is-native .cp-page .cp-profil__photo {
  position: relative;
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
}

body.is-native .cp-page .cp-profil__photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 2px solid var(--cp-red);
  border-radius: 36px;
  background: var(--cp-raised);
}

body.is-native .cp-page .cp-profil__initiales {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 26px;
  letter-spacing: 1px;
  color: var(--cp-on-dark);
  background: var(--cp-dark);
  border: 2px solid var(--cp-red);
  border-radius: 36px;
}

body.is-native .cp-page .cp-profil__edit {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border: 2px solid var(--cp-surface);
  border-radius: 14px;
  cursor: pointer;
}

body.is-native .cp-page .cp-profil__texte {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

body.is-native .cp-page .cp-profil__nom {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 22px;
  line-height: 1.1;
  color: var(--cp-ink);
}

body.is-native .cp-page .cp-profil__mail {
  font-size: 13px;
  font-weight: 600;
  color: var(--cp-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- Formulaire ---------- */

body.is-native .cp-page .cp-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
  padding: 18px 16px;
  background: var(--cp-surface);
  border: 1px solid var(--cp-hair);
  border-radius: 20px;
}

body.is-native .cp-page .cp-champ {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

body.is-native .cp-page .cp-champ__label {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--cp-ink);
}

body.is-native .cp-page .cp-input {
  width: 100%;
  height: 46px;
  padding: 0 14px;
  /* 16px minimum : en dessous, iOS agrandit la page à la mise au point. */
  font-family: 'Dosis', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--cp-ink);
  background: var(--cp-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  border-radius: 12px;
  -webkit-user-select: text;
  user-select: text;
  transition: border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              background 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-input:focus {
  border-color: var(--cp-red);
  background: #FAF5EB;
}

body.is-native .cp-page .cp-enregistrer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  margin-top: 4px;
  font-size: 17px;
  font-weight: 800;
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(221, 65, 36, 0.32);
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-enregistrer:active { transform: scale(0.97); }

/* ---------- Préférences ---------- */

body.is-native .cp-page .cp-prefs {
  margin-bottom: 18px;
  padding: 18px 16px;
  background: var(--cp-surface);
  border: 1px solid var(--cp-hair);
  border-radius: 20px;
}

body.is-native .cp-page .cp-prefs__titre {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 18px;
  text-transform: uppercase;
  color: var(--cp-ink);
}

body.is-native .cp-page .cp-prefs__note {
  margin: 4px 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--cp-ink-2);
}

body.is-native .cp-page .cp-prefs__pastilles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

body.is-native .cp-page .cp-pref {
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 700;
  color: var(--cp-ink-2);
  background: var(--cp-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  border-radius: 19px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-pref.is-on {
  color: var(--cp-on-dark);
  background: var(--cp-red);
  border-color: var(--cp-red);
}

/* ---------- Pied d'écran ---------- */

body.is-native .cp-page .cp-pied {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px dashed rgba(107, 94, 84, 0.22);
}

body.is-native .cp-page .cp-liens {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 18px;
}

body.is-native .cp-page .cp-liens a {
  font-size: 13px;
  font-weight: 600;
  color: var(--cp-ink-3);
  text-decoration: underline;
  cursor: pointer;
}

body.is-native .cp-page .cp-deco {
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 800;
  color: var(--cp-red);
  border: 1.5px solid var(--cp-red);
  border-radius: 21px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .cp-page .cp-deco:active {
  color: var(--cp-on-dark);
  background: var(--cp-red);
}

body.is-native .cp-page .cp-mentions {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: var(--cp-ink-3);
}

@media (prefers-reduced-motion: reduce) {
  body.is-native .cp-page .cp-onglets__marque { transition: none; }
}
</style>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import { isNative } from '@/api/base';
import { pickProfileImage } from '@/native/camera';

export default {
  data() {
    return {
      activeSection: 'info',
      showModal: false,
      selectedOffer: {},
      showConfirmationModal: false, 
      confirmationMessage: '',
      showStripeModal: false,        
      stripe: null,                
      cardElement: null,            
      cardErrors: '',               
      clientSecret: '',             
      publicKey: '', 
      creditAmount: 10,
      stripeReady: false,
    };
  },
  methods: {
    
    parseFrenchDate(frenchDate) {
    const norm = frenchDate
       .trim()
       .toLowerCase()
       .normalize('NFD')
       .replace(/[\u0300-\u036f]/g, '');
     const [day, monthName, year] = norm.split(' ');
     const monthMap = {
       janvier:0, fevrier:1, mars:2, avril:3, mai:4, juin:5,
       juillet:6, aout:7, septembre:8, octobre:9, novembre:10, decembre:11
     };
     return new Date(+year, monthMap[monthName], +day);
   },

   parseOfferDate(dateStr) {
     if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
       return new Date(dateStr);
     }
     if (dateStr.includes(' au ')) {
       const [start] = dateStr.split(' au ');
       return this.parseFrenchDate(start);
     }
     return this.parseFrenchDate(dateStr);
   },
    decodeHtml(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  },
  async handleStripeSetup() {
  if (!this.creditAmount || this.creditAmount <= 0) {
    this.cardErrors = "Veuillez entrer un montant valide.";
    return;
  }

  this.cardErrors = '';
  this.$nextTick(async () => {
    await this.createPaymentIntentForCredits();
  });
  this.stripeReady = true; 
},
closeStripeModal() {
  this.showStripeModal = false;
  this.stripeReady = false;
  this.cardErrors = '';
},
  openStripeModal() {
      this.showStripeModal = true;
      this.cardErrors = '';
      this.creditAmount = 10; 
      this.createPaymentIntentForCredits();
    },
  

    async createPaymentIntentForCredits() {
      try {
        const formData = new FormData();
        formData.append('userId', this.userId);
        formData.append('amount', this.creditAmount); 

        const response = await axios.post(
          'https://www.ouicsport.fr/api/profile/recharge-credits',
          formData
        );
        this.clientSecret = response.data.data.pi_client_secret;
        this.publicKey = response.data.data.pk_key;
        this.setupStripe();
      } catch (error) {
        console.error('Erreur creation PaymentIntent crédits :', error);
      }
    },

    setupStripe() {
      this.stripe = window.Stripe(this.publicKey); 
      const elements = this.stripe.elements();
      const style = {
        base: {
          color: '#32325d',
          fontWeight: 500,
          fontFamily: "Source Sans Pro, Consolas, Menlo, monospace",
          fontSize: "16px",
          fontSmoothing: "antialiased",
          '::placeholder': {
            color: '#CFD7DF'
          }
        },
        invalid: {
          iconColor: '#e85746',
          color: '#e85746'
        }
      };
      this.cardElement = elements.create('card', { style });
      this.cardElement.mount('#card-element');
      this.cardElement.on('change', (event) => {
        if (event.error) {
          this.cardErrors = event.error.message;
        } else {
          this.cardErrors = '';
        }
      });
    },

    async payWithCard() {
      if (!this.stripe || !this.clientSecret) {
        this.cardErrors = "Stripe n'est pas prêt. Réessayez.";
        return;
      }
      try {
        const { paymentIntent, error } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          {
            payment_method: {
              card: this.cardElement,
            },
          }
        );
        if (error) {
          this.cardErrors = error.message;
          return;
        }
        if (paymentIntent && paymentIntent.status === 'succeeded') {
          await this.finalizeCreditPurchase(paymentIntent.id);
        }
      } catch (err) {
        console.error('Erreur Stripe confirmCardPayment:', err);
        this.cardErrors = 'Une erreur est survenue lors du paiement.';
      }
    },

    async finalizeCreditPurchase(paymentIntentId) {
      try {
        const formData = new FormData();
        formData.append('userId', this.userId);
        formData.append('paymentIntentId', paymentIntentId);
        formData.append('amount', this.creditAmount);

        await axios.post(
          'https://www.ouicsport.fr/api/profile/recharge-credits',
          formData
        );
        this.closeStripeModal();
        await this.fetchCreditsBalance();
        alert('Bravo, votre compte a été crédité avec succès !');
      } catch (error) {
        console.error('Erreur finalisation crédit :', error);
        this.cardErrors = 'Problème lors de l’ajout des crédits.';
      }
    },

  openConfirmationModal() {
    const formData = new URLSearchParams();
    formData.append('userId', this.userId);
    formData.append('purchaseId', this.selectedOffer.id);

    axios.post('https://www.ouicsport.fr/api/offer/cancelWeb', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then(response => {
        this.confirmationMessage = response.data.message;
        this.showConfirmationButtons = response.data.showConfirmationButtons; // Gérer l'affichage des boutons
        this.showConfirmationModal = true;
      })
      .catch(error => {
        console.error('Erreur lors de l\'annulation :', error);
      });
  },


  confirmCancellation() {
      const formData = new URLSearchParams();
      formData.append('userId', this.userId);
      formData.append('purchaseId', this.selectedOffer.id);

      axios.post('https://www.ouicsport.fr/api/offer/cancelWeb/confirmation', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(() => {
        this.closeConfirmationModal();
        window.location.reload(); 
      })
      .catch(error => {
        console.error('Erreur lors de la confirmation :', error);
      });
    },

    closeModal() {
      this.showModal = false;
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    setActiveSection(section) {
      this.activeSection = section;
    },
    isOfferPassed(offer) {
      const today = new Date();
      const offerDate = new Date(offer.date);
      return offerDate < today || offer.date.includes('Finished');
    },
    
  logout() {
    this.$store.dispatch('auth/logout') 
      .then(() => {
        this.$router.push('/login');     
      })
      .catch(error => {
        console.error('Erreur lors de la déconnexion :', error);
      });
  },
  navigateToOfferDetails(offerId) {
      this.selectedOffer = this.html.purchases.find(offer => offer.id === offerId); 
      this.showModal = true;
      const formattedDate = new Date(this.selectedOffer.date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(',', '').trim();
      const formattedDateCorrect = formattedDate.split(' ').join('%20').replace(/\//g, '%2F');
      this.selectedOffer.ID_Criteria = `${this.selectedOffer.offerId}%20${formattedDateCorrect}`;
      this.selectedOffer.ID_Criteria = decodeURIComponent(this.selectedOffer.ID_Criteria);
      this.selectedOffer.ID_Criteria = this.selectedOffer.ID_Criteria.replace("01:00", "00:00");
  },


  },
  computed: {
    /* Accesseurs sûrs, utilisés par l'écran natif uniquement.
       `upcomingOffers` et `pastOffers` lisent `html.purchases`, qui n'existe
       qu'une fois la réponse arrivée : les évaluer plus tôt — l'onglet natif
       affiche un compteur dès le premier rendu — lève une erreur et laisse
       l'écran blanc. Le site n'y était pas exposé : il n'ouvre l'onglet
       Réservations que sur clic, donc toujours après le chargement. */
    /* Photo de profil réelle, ou rien.
       `photoBackgroundStyle` retombe sur une image hébergée sur un blog
       WordPress tiers (filmshotfreezer.files.wordpress.com, 2011) quand
       l'utilisateur n'en a pas déposé. Dépendre d'un domaine que personne ici
       ne contrôle pour l'avatar de TOUS les comptes sans photo n'est pas
       acceptable dans une application : on affiche les initiales à la place. */
    cpPhoto() {
      const url = this.photoBackgroundStyle;
      if (!url || String(url).includes('wordpress.com')) return null;
      return url;
    },
    cpInitiales() {
      return [this.firstname, this.lastname]
        .filter(Boolean)
        .map((m) => String(m).trim().charAt(0).toUpperCase())
        .join('') || '?';
    },

    cpAVenir() {
      return (this.html && this.html.purchases) ? this.upcomingOffers : [];
    },
    cpPassees() {
      return (this.html && this.html.purchases) ? this.pastOffers : [];
    },

    upcomingOffers() {
    const today = new Date();
    return this.html.purchases.filter(offer => {
      const offerDate = this.parseOfferDate(offer.date);

      if (offerDate >= today && !offer.date.includes('Finished')) {
        offer.formattedDate = offer.date.includes(' au ')
           ? offer.date
           : offerDate.toLocaleDateString('fr-FR', {
               weekday: 'long',
               day:     'numeric',
               month:   'long'
             });

        return true;
      }
      return false;
    });
  },
  pastOffers() {
    const today = new Date();
    return this.html.purchases.filter(offer => {
      const offerDate = this.parseOfferDate(offer.date);

      if (offerDate < today || offer.date.includes('Finished')) {
        offer.formattedDate = offer.date.includes(' au ')
           ? offer.date
           : offerDate.toLocaleDateString('fr-FR', {
               weekday: 'long',
               day:     'numeric',
               month:   'long'
             });
        return true;
      }
      return false;
    });
  }
},
  setup() {
    const store = useStore();
    const userId = ref(store.state.auth.user.data.id);
    const lastname = ref(store.state.auth.user.data.lastname);
    const firstname = ref(store.state.auth.user.data.firstname);
    const email = ref(store.state.auth.user.data.email);
    const phone = ref(store.state.auth.user.data.phone);
    const birthdate = ref(store.state.auth.user.data.birthdate);
    const preferredOffers = ref(store.state.auth.user.data.preferredOffers);
    const preferredPlace = ref(store.state.auth.user.data.preferredPlace);
    const preferredLatitude = ref(store.state.auth.user.data.preferredLatitude);
    const preferredLongitude = ref(store.state.auth.user.data.preferredLongitude);
    const perimeter = ref(store.state.auth.user.data.perimeter);
    const preferredSport = ref(store.state.auth.user.data.preferredSports);
    const photo = ref(store.state.auth.user.data.photo);
    const rawHtml = ref('');
    const creditsBalance = ref(0);
    const sports = ref([]);
    const types = ref([]);
    const selectedSport = ref('');
    const selectedType = ref('');
    const html = ref([]);
    const file = ref(null);
    const fileInput = ref(null);


    const photoBackgroundStyle = computed(() => {
      if (photo.value && photo.value.startsWith('http')) {
        return `${photo.value}`;
      } else {
        return 'https://filmshotfreezer.files.wordpress.com/2011/07/untitled-1.jpg';
      }
    });

    const handleFileChange = () => {
      handleFileUpload();
      submitProfileImage();
    };

    /**
     * Photo de profil dans l'application native.
     *
     * Le champ fichier reste le chemin du site web. Dans l'app, on passe par le
     * sélecteur natif : il propose « Prendre une photo » ou « Depuis la
     * galerie » au lieu d'un explorateur de fichiers, et surtout il
     * recompresse l'image à 85 % avant l'envoi — les photos d'un appareil
     * moderne pèsent plusieurs mégaoctets, ce qui rendait l'envoi interminable
     * en 4G.
     *
     * La conversion se fait à la main plutôt qu'avec fetch(dataUrl) : dans
     * l'app, CapacitorHttp remplace window.fetch et détourne les URL qu'il ne
     * reconnaît pas vers la couche HTTP native, où une URL `data:` n'a aucun
     * sens.
     */
    const dataUrlToFile = (dataUrl, filename) => {
      const [meta, base64] = dataUrl.split(',');
      const mime = (/:(.*?);/.exec(meta) || [])[1] || 'image/jpeg';
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new File([bytes], filename, { type: mime });
    };

    const chooseNativePhoto = async () => {
      const dataUrl = await pickProfileImage();
      if (!dataUrl) return; // l'utilisateur a annulé
      file.value = dataUrlToFile(dataUrl, 'photo-profil.jpg');
      await submitProfileImage();
    };

    const handleFileUpload = () => {
      file.value = fileInput.value.files[0];
    };

    const submitProfileImage = async () => {
      try {
        const response = await store.dispatch('auth/uploadProfileImage', {
          userId: userId.value,
          file: file.value
        });
        
        if (response && response.data && response.data.photo) {
          store.commit('SET_USER_PHOTO', response.data.photo);
          photo.value = response.data.photo;  
        }
        window.location.reload();

      } catch (error) {
        console.error('Erreur lors du téléchargement de l\'image :', error);
      }
    };

    const submit = async () => {
      await store.dispatch('auth/updateProfile', {
        userId: userId.value,
        lastname: lastname.value,
        firstname: firstname.value,
        email: email.value,
        phone: phone.value,
        birthdate: birthdate.value,
        // Other optional fields can be added here
      });
    };

    const fetchOffers2 = async () => {
      try {

        const response = await axios.get(`https://www.ouicsport.fr/api/offer/my-booked-offers-json?userId=${userId.value}`);
        //const response = await axios.get(`https://www.ouicsport.fr/api/offer/my-booked-offers-json?userId=5296`);
        let cleanedData = response.data.replace(/(\r\n|\n|\r)/gm, ""); // Supprime les retours à la ligne
        cleanedData = cleanedData.trim();
        html.value = JSON.parse(cleanedData);
        html.value.purchases.forEach(offer => {
          if (offer.criteria && offer.criteria.length > 0) {
            const firstCriterion = offer.criteria[0];
            offer.ID_Criteria = `${offer.id}-${firstCriterion.date}`; // ID unique basé sur l'offre et la première date
          }
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des offres:', error);
      }
    };

    fetchOffers2();



    const fetchSportsAndTypes = async () => {
      const data = await store.dispatch('user/fetchSportsAndTypes');
      sports.value = data.sports;
      types.value = data.types;
    };
    fetchSportsAndTypes();
    const fetchCreditsBalance = async () => {
      try {
        const response = await axios.get(`https://www.ouicsport.fr/api/profile/credits-balance?userId=${userId.value}`);
        creditsBalance.value = response.data.data; 
      } catch (error) {
        console.error('Erreur récupération du solde de crédits :', error);
        creditsBalance.value = 0;
      }
    };
    fetchCreditsBalance();


    const updateProfile = async () => {
      const formData = new FormData();
  
  // Fonction pour ajouter des clés/valeurs à formData, en remplaçant les valeurs nulles ou les tableaux vides par des chaînes vides
  const appendFormData = (key, value) => {
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          formData.append(key, '');
        } else {
          formData.append(key, value);
        }
      };

      appendFormData('userId', userId.value);
      appendFormData('email', email.value);
      appendFormData('lastname', lastname.value);
      appendFormData('firstname', firstname.value);
      appendFormData('phone', phone.value);
      appendFormData('birthdate', birthdate.value);
      appendFormData('preferredSports', preferredSport.value);
      appendFormData('preferredPlace', preferredPlace.value);
      appendFormData('preferredLatitude', preferredLatitude.value);
      appendFormData('preferredLongitude', preferredLongitude.value);
      appendFormData('preferredOffers', preferredOffers.value);
      appendFormData('perimeter', perimeter.value || 1000);

      try {
      await axios.post('/api/profile/update', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        store.state.auth.user.data.lastname = lastname.value;
        store.state.auth.user.data.firstname = firstname.value;
        store.state.auth.user.data.phone = phone.value;
        store.state.auth.user.data.birthdate = birthdate.value;
        store.state.auth.user.data.preferredSports = preferredSport.value;
        store.state.auth.user.data.preferredPlace = preferredPlace.value;
        store.state.auth.user.data.preferredLatitude = preferredLatitude.value;
        store.state.auth.user.data.preferredLongitude = preferredLongitude.value;
        store.state.auth.user.data.preferredOffers = preferredOffers.value;
        store.state.auth.user.data.perimeter = perimeter.value;

      } catch (error) {
        // Gérer l'erreur ici
        console.error('Erreur lors de la mise à jour du profil:', error);
      }
    };

    return { isNative, html,fileInput, handleFileChange, chooseNativePhoto, rawHtml,creditsBalance ,photoBackgroundStyle, preferredSport, sports, types, selectedSport, selectedType, userId, lastname, firstname, email, phone, birthdate, preferredOffers, submit, updateProfile };
  }
}
</script>
